import { z } from 'zod';
import { isPermission } from './permissions.js';

/** Request validation shared by the API (authoritative) and the web app (instant feedback). */

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(320)
  .pipe(z.email({ message: 'Enter a valid email address' }));

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH, `Use at most ${PASSWORD_MAX_LENGTH} characters`);

const nameSchema = z.string().trim().min(1, 'Required').max(120);

export const registerSchema = z
  .object({
    name: nameSchema,
    email: emailSchema,
    password: passwordSchema,
  })
  .refine((v) => v.password.toLowerCase() !== v.email, {
    message: 'Password must not be your email address',
    path: ['password'],
  });
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const tokenSchema = z.object({ token: z.string().min(20).max(200) });

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  newPassword: passwordSchema,
});

export const totpCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});

export const mfaVerifySchema = z
  .object({
    code: z.string().trim().max(32).optional(),
    recoveryCode: z.string().trim().max(32).optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), {
    message: 'Provide either a code or a recovery code',
  });

export const mfaDisableSchema = z.object({
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});

export const PRICING_REGIONS = ['india', 'global'] as const;

/**
 * Accepts any IANA zone the runtime knows, including aliases: ICU's canonical list contains
 * `Asia/Calcutta` but not `Asia/Kolkata`, so a list lookup would reject valid zones.
 */
export function isTimeZone(value: string): boolean {
  if (!value) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

// Lazily built so importing this module stays cheap in the browser.
let currencies: Set<string> | undefined;
export function isCurrency(value: string): boolean {
  currencies ??= new Set(Intl.supportedValuesOf('currency'));
  return currencies.has(value);
}

export const createTenantSchema = z.object({
  name: z.string().trim().min(2, 'Use at least 2 characters').max(120),
  country: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, 'Use a 2-letter country code')
    .transform((v) => v.toUpperCase()),
  timezone: z.string().trim().max(64).refine(isTimeZone, 'Unknown time zone'),
  defaultCurrency: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{3}$/, 'Use a 3-letter currency code')
    .transform((v) => v.toUpperCase())
    .refine(isCurrency, 'Unknown currency'),
});
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

export const updateTenantSchema = createTenantSchema.partial();

export const switchTenantSchema = z.object({ tenantId: z.string().length(26) });

export const inviteMemberSchema = z.object({
  email: emailSchema,
  roleId: z.string().length(26),
});

export const updateMemberSchema = z.object({
  roleId: z.string().length(26).optional(),
  status: z.enum(['active', 'suspended']).optional(),
});

const permissionKeySchema = z.string().refine(isPermission, { message: 'Unknown permission' });

export const roleKeySchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[a-z][a-z0-9_-]*$/, 'Lowercase letters, numbers, - and _ only');

export const createRoleSchema = z.object({
  key: roleKeySchema,
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(255).default(''),
  permissions: z.array(permissionKeySchema).max(100),
});

export const updateRoleSchema = createRoleSchema.omit({ key: true }).partial();

export const paginationSchema = z.object({
  cursor: z.string().length(26).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
